import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Collapse } from 'react-collapse';
import _ from 'lodash';
import { skipToken } from '@reduxjs/toolkit/query';
import moment from 'moment';
import type { LogArgument } from 'rollbar';
import { Link, useHistory, useLocation } from '@bbkShared/react-router';
import { cs } from '@bbkAdminUtils/css-utils';
import { useAppDispatch } from '@bbkAdminRedux/redux_utils';
import { useCreatePagesListUrl, useUrl } from '@bbkAdminUtils/nav';
import internalMenu from '@bbkAdminComponents/internal/internal-pages';
import BrightbackSession from '@bbkAdminUtils/brightback-session';
import { ProfileImage } from '@bbkAdminComponents/profile/profile-image';
import { CompanyLogo } from '@bbkAdminComponents/profile/company-logo';
import LinkListPopover from '@bbkAdminComponents/popovers/link-list-popover';
import { ProductTourModal } from '@bbkAdminComponents/product-tour/product-tour-modal';
import type LinkGroupMenu from '@bbkAdminComponents/linkGroup/link-group';
import {
  selectApp,
  selectAppId,
  selectCompany,
  selectCompanyInternalName,
  selectUser,
} from '@bbkAdminRedux/app/selectors';
import {
  useGetProductLicense,
  useHasSubscription,
  usePlans,
} from '@bbkAdminUtils/plans';
import { useStrings } from '@bbkAdminUtils/strings';
import { AuthorizedPaths } from '@bbkAdminRouter/router-paths';
import {
  useDaysToExpiration,
  useSelectCompanySubscription,
} from '@bbkAdminRedux/rtkq/checkout.slice';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import {
  BottomAlertTab,
  NavCollapseContainer,
  NavContainer,
  NavHeader,
  NavIcons,
  NavItem,
  NavLink,
  NavSubHeader,
} from '@bbkAdminComponents/app-layout/nav';
import {
  CBGrowthLogoSVG,
  CBRetentionLogo,
} from '@bbkAdminComponents/cbretention-logo';
import { useSelectBillingIntegration } from '@bbkAdminRedux/rtkq/integrations.slice';
import { billingConfigSlice } from '@bbkAdminRedux/rtkq/billing-config.slice';
import { GetStartedNav } from '@bbkAdminComponents/get-started/get-started-nav';
import { SuspenseLoader } from '@bbkAdminComponents/loaders/suspense-loader';
import { selectCompanyMisc } from '@bbkAdminRedux/rtkq/app-persist/app-persist.slice';
import { useSelectCurrentApp } from '@bbkAdminRedux/rtkq/current-user.slice';
import { TippyTooltip } from '@bbkAdminComponents/tooltips/tippy-tooltip';
import { useGetStartedStatus } from '@bbkAdminComponents/get-started/get-started-page';
import { useQueryFeatureFlags } from '@bbkAdminRedux/rtkq/feature-flags.slice';
import { appNotPersistedSlice } from '@bbkAdminRedux/rtkq/app-not-persisted.slice';
import {
  ONBOARDING_HISTORY_STEP,
  onboardingHistorySlice,
  selectOnboardingHistoryMap,
} from '@bbkAdminRedux/rtkq/onboarding-history.slice';
import {
  determineLeftNavAppearance,
  determineSelectedNavItem,
} from '@bbkAdminRedux/app/reducers';
import { recordError } from '@bbkAdminUtils/recordError';
import { useRoles } from '@bbkAdminUtils/roles';
import { IntegrationDataType } from '@bbkAdminRedux/data-management/reducers';
import { CBProductsSwitcher } from '@bbkAdminComponents/CBProductsSwitcher';
import { brightbackAuthSlice } from '@bbkAdminRedux/rtkq/brightback-auth.slice';
import icons from '@bbkAdminUtils/icons';
import { invitationSlice } from '@bbkAdminRedux/rtkq/invitation.slice';
import { LogInPaths } from '@bbkAdminComponents/log-in-flow/log-in-paths';

const WelcomePopup = React.lazy(
  () => import('@bbkAdminComponents/get-started/welcome-popup')
);

type NavigationLinkOption = {
  title: React.ReactNode;
  icon?: React.ReactElement;
  path: string;
  isActive: boolean;
  show: boolean;
  disabled?: boolean;
  disableTooltipText?: React.ReactNode;
  key: string;
  linkAttributes?: { title: string };
};

type NavigationItemOption = {
  title: React.ReactNode;
  icon: React.ReactNode;
  onClick: () => void;
  isActive: boolean;
  disabled?: boolean;
  disableTooltipText?: React.ReactNode;
  show: boolean;
  expandChildren: boolean;
  children: Array<NavigationItemOption | NavigationLinkOption>;
  key: string;
};

const withToolTip = (
  content: React.ReactNode,
  menuName: React.ReactElement
): React.ReactElement => (
  <TippyTooltip
    content={content}
    tippyOptions={{
      placement: 'right-start',
      appendTo: () => document.body,
      zIndex: 10,
    }}
  >
    {menuName}
  </TippyTooltip>
);

type LinkGroupMenuItem = React.ComponentProps<
  typeof LinkGroupMenu
>['links'][number];
const LeftColumn: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const { data: featureFlags } = useQueryFeatureFlags();
  const dispatch = useAppDispatch();
  const app = useSelector(selectApp);
  const currentApp = useSelectCurrentApp();
  const company = useSelector(selectCompany);
  const user = useSelector(selectUser);
  const selectedNavItem = determineSelectedNavItem(location.pathname);
  const showNav = determineLeftNavAppearance(location.pathname);
  const createUrl = useUrl();
  const [signOut$] = brightbackAuthSlice.endpoints.signOut.useMutation();
  const { data: pendingInvites = [] } =
    invitationSlice.endpoints.getPendingInvitationsByEmail.useQuery();

  const showWhatsNew = useFeatureFlags(FeatureFlagsE.SHOW_WHATS_NEW);
  const showRetentionLogoOnAGS = useFeatureFlags(
    FeatureFlagsE.SHOW_RETENTION_LOGO_ON_AGS
  );
  const expiration = useDaysToExpiration();

  const userAnchorRef = useRef<HTMLDivElement>(null);
  const appAnchorRef = useRef<HTMLDivElement>(null);

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [appMenuOpen, setAppMenuOpen] = useState(false);
  const [trendsExpanded, setTrendsExpanded] = useState(false);
  const [customersExpanded, setCustomersExpanded] = useState(false);
  const [internalExpanded, setInternalExpanded] = useState(false);
  const [experiencesExpanded, setExperiencesExpanded] = useState(false);
  const [insightsExpanded, setInsightsExpanded] = useState(false);
  const [showInternal, setShowInternal] = useState(false);
  const [reportsExpanded, setReportsExpanded] = useState(false);

  const { isSuccess: isFFsSuccess } = useQueryFeatureFlags();

  const appKey = useSelector(selectAppId);
  const companyInternalName = useSelector(selectCompanyInternalName);
  const hasSubscription = useHasSubscription();
  const subscription = useSelectCompanySubscription(
    hasSubscription
      ? {
          company: companyInternalName,
        }
      : skipToken
  );
  const billingIntegration = useSelectBillingIntegration({ appKey });
  const hasChargebeeIntegration =
    billingIntegration?.data_type === IntegrationDataType.CHARGEBEE;

  const productLicense = useGetProductLicense();
  const agsLicense = productLicense.agsLicense;
  const deflectLicense = productLicense.deflectLicense;

  const { data: billingConfig } =
    billingConfigSlice.endpoints.getBillingConfig.useQuery(
      billingIntegration ? { app_key: appKey } : skipToken
    );
  const isRepurchaseAttributionEnabled =
    billingConfig?.repurchase_based_attribution;

  const { data: steps = {} } =
    onboardingHistorySlice.endpoints.getOnboardingHistorySteps.useQuery(
      { appKey },
      {
        skip: appKey === '_' || app?.get_started_status !== 'ELIGIBLE',
        selectFromResult: selectOnboardingHistoryMap,
      }
    );
  const isGetStartedEligible = currentApp?.get_started_status === 'ELIGIBLE';
  const { isTestAndLaunchCompleted, isCreateExperienceCompleted } =
    useGetStartedStatus();
  const showGetStarted = isGetStartedEligible;
  const isCreateOrLaunchCompleted =
    isCreateExperienceCompleted || isTestAndLaunchCompleted;
  const isExpLibraryDisabled = showGetStarted && !isCreateOrLaunchCompleted;
  const isCreateOrLaunchCompletedInPast =
    steps[ONBOARDING_HISTORY_STEP.BRANDING] ||
    steps[ONBOARDING_HISTORY_STEP.GO_LIVE];
  const isExpBrandingLibDisabled = isExpLibraryDisabled
    ? showGetStarted && !isCreateOrLaunchCompletedInPast
    : isExpLibraryDisabled;
  const pagesListUrl = useCreatePagesListUrl(
    !isCreateOrLaunchCompleted && isCreateOrLaunchCompletedInPast
  );

  const showWizardPopupFF = useFeatureFlags(FeatureFlagsE.SHOW_WELCOME_POPUP);
  const additionalBrandsEnabled = useFeatureFlags(
    FeatureFlagsE.ADDITIONAL_BRANDS_ENABLED
  );

  const isLifecycleOffersEnabled = useFeatureFlags(
    FeatureFlagsE.ENABLE_LIFECYCLE_OFFERS
  );
  const playEnabled = isLifecycleOffersEnabled || agsLicense;
  const isCancelInsightsAIEnabled = useFeatureFlags(
    FeatureFlagsE.ENABLE_LLM_FEEDBACK_CLASSIFIER
  );
  const { showWelcomePopup } = useSelector(
    selectCompanyMisc(companyInternalName)
  );

  // Plan filters
  const { isTrial, isStarterFreePlan, isStarterPremiumPlan } = usePlans();
  // show welcome popup once per user for trial/starter
  const isStarter = isStarterFreePlan || isStarterPremiumPlan;
  const showWizardPopupEligible = isTrial || showWizardPopupFF || isStarter;
  const showWizardPopup =
    appKey !== '_' && showWizardPopupEligible && showWelcomePopup;
  const strings = useStrings();

  const { hasBillingRole } = useRoles();
  const showAlert = useMemo(() => {
    if (!subscription?.price.included_session_limit) return false;
    return (
      hasSubscription &&
      hasBillingRole &&
      subscription?.usage > subscription?.price.included_session_limit
    );
  }, [isTrial, hasSubscription, subscription, hasBillingRole]);

  const regexTest = useCallback(
    (regex: string | RegExp): boolean => {
      // test is general, regex is the string to test for
      // i.e. trendsReport, trends => true
      const routeRegex = new RegExp(regex);
      return routeRegex.test(selectedNavItem);
    },
    [selectedNavItem]
  );

  useEffect(() => {
    if (agsLicense && !deflectLicense) {
      if (
        regexTest(AuthorizedPaths.dashboard) &&
        !regexTest(AuthorizedPaths.viewPredictiveCustomerPreview)
      ) {
        history.push(createUrl(AuthorizedPaths.plays));
      }
    }
  }, [
    agsLicense,
    createUrl,
    deflectLicense,
    history,
    location.pathname,
    regexTest,
  ]);

  useEffect(() => {
    setTrendsExpanded(regexTest('trends'));
    setCustomersExpanded(regexTest('customers'));
    setInternalExpanded(regexTest('internal'));
    setInsightsExpanded(regexTest('insights'));
    if (agsLicense) {
      setExperiencesExpanded(!regexTest('offers') && regexTest('experiences'));
    } else {
      setExperiencesExpanded(regexTest('experiences'));
    }
    setReportsExpanded(
      regexTest('trends') || regexTest('insights') || regexTest('customers')
    );
  }, [regexTest]);

  useEffect(() => {
    let localShow = false;
    if (internalMenu && internalMenu.length && featureFlags) {
      localShow = internalMenu.reduce(
        (previousValue, item) =>
          previousValue ||
          item.featureFlags.filter((ff) => featureFlags.includes(ff)).length >
            0,
        false
      );
    }
    setShowInternal(localShow);
  }, [featureFlags]);

  if (!user && !company) {
    return null;
  }
  const appCount = user.companies.reduce(
    (ret, c) => ret + c.applications.length,
    0
  );
  const enableAppSwitcher = user.companies.length > 1 || appCount > 1;

  const switchApp = (companyKey: string, localAppKey: string): void => {
    const pathname = window.location.pathname;
    let splitPath = pathname.split('/').slice(5);
    if (splitPath[0] === 'experiences' || splitPath[0] === 'settings') {
      splitPath = splitPath.slice(0, 1);
    }
    const currentPath = splitPath.join('/');
    const url = `/company/${companyKey}/app/${localAppKey}/${currentPath}`;
    BrightbackSession.setActiveApp({ encoded_id: localAppKey });
    BrightbackSession.setActiveCompany({ internal_name: companyKey });
    window.location.assign(url);
  };

  const userMenuGroupItems: LinkGroupMenuItem[] = [
    ...(user.user_profile_url
      ? [
          {
            icon: 'profile',
            text: 'Profile',
            link: user.user_profile_url,
          } satisfies LinkGroupMenuItem,
        ]
      : []),
    ...(pendingInvites.length
      ? [
          {
            icon: 'email',
            text: `Pending invites (${pendingInvites.length})`,
            link: LogInPaths.selector,
            iconClasses: '[&_svg]:tw-w-3.5 [&_svg]:tw-h-3.5 tw-pl-0.5',
          } satisfies LinkGroupMenuItem,
        ]
      : []),
    {
      icon: 'lock',
      text: 'Sign out',
      linkAction: (): void => {
        const { pathname, search, hash } = window.location;
        signOut$({
          redirect: pathname + search + hash,
        })
          .unwrap()
          .catch((err: LogArgument) => {
            recordError(err);
          });
      },
      classes: 'logout',
      iconClasses: 'tw-w-3.5',
    },
  ];

  if (showWhatsNew) {
    userMenuGroupItems.splice(0, 0, {
      icon: 'whatsNew',
      text: "What's new?",
      linkAction: (): void => {},
      classes: 'logout',
      iconClasses: 'tw-w-3.5',
    });
  }
  const appMenuGroupItems: LinkGroupMenuItem[] = [];

  const sortedCompanies = _.orderBy(
    user.companies,
    [(v) => v.name.toLowerCase()],
    ['asc']
  );

  sortedCompanies.forEach((c) => {
    appMenuGroupItems.push({
      text: c.name,
      itemClasses: 'customerData',
      company: c,
    });
    if (!c.applications || c.applications.length === 0) {
      appMenuGroupItems.push({
        text: '(No Access)',
        classes: 'indent disabled',
        itemClasses: 'customerData',
        company: c,
        isApplication: true,
      });
    } else {
      c.applications.forEach((a) => {
        appMenuGroupItems.push({
          text: a.display_name,
          linkAction: () => switchApp(c.internal_name, a.encoded_id),
          classes: 'indent',
          itemClasses: 'customerData',
          company: c,
          app: a,
          isApplication: true,
        });
      });
    }
  });

  const expired = expiration
    ? expiration.endDate.diff(moment.utc(), 'days') < 0
    : false;

  const helpDocSection = (
    <NavContainer>
      <NavLink
        classes={cs({ active: regexTest('help') }, 'nav-link')}
        href="https://www.chargebee.com/docs/retention"
        text="Help docs"
        iconNode={<span>{NavIcons.reference}</span>}
        linkAttributes={{
          target: '_blank',
          rel: 'noopener noreferrer',
        }}
      />
    </NavContainer>
  );

  const contactSupportSection = (
    <NavContainer>
      <NavItem
        onClick={() => undefined}
        classes="nav-link"
        text={
          <a href="mailto:retention-support@chargebee.com">Contact support</a>
        }
        icon="contactSupport"
      />
    </NavContainer>
  );

  const appSwitcherSection = enableAppSwitcher && company && (
    <>
      <NavContainer>
        <div ref={appAnchorRef}>
          <NavItem
            classes="nav-link app-menu"
            onClick={() => setAppMenuOpen(true)}
            iconNode={
              <CompanyLogo
                className="tw-ml-0.5 tw-w-5 tw-rounded-full"
                company={company}
              />
            }
            content={
              <>
                {appCount > 1 && (
                  <div className="company-name">{company.name}</div>
                )}
                <div className="env" title={company.name}>
                  {app?.display_name ?? 'Default'}
                </div>
              </>
            }
          />
        </div>
      </NavContainer>
      <LinkListPopover
        open={appMenuOpen}
        linkGroupItems={appMenuGroupItems}
        handleClose={() => setAppMenuOpen(false)}
        anchorEl={appAnchorRef.current}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 10,
        }}
        enableSearch
      />
    </>
  );

  const userProfileSection = (
    <>
      <NavContainer>
        <div ref={userAnchorRef}>
          <NavItem
            classes={cs('nav-link')}
            onClick={() => setUserMenuOpen(true)}
            iconNode={<ProfileImage user={user} />}
            text={
              <>
                {user.details.display_name}
                {pendingInvites.length ? (
                  <TippyTooltip
                    content={`You have ${pendingInvites.length} pending invites.`}
                  >
                    <span className="tw-ml-1 tw-rotate-12">{icons.bell}</span>
                  </TippyTooltip>
                ) : null}
              </>
            }
          />
        </div>
      </NavContainer>
      <LinkListPopover
        open={userMenuOpen}
        linkGroupItems={userMenuGroupItems}
        handleClose={() => setUserMenuOpen(false)}
        anchorEl={userAnchorRef.current}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'top',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 10,
        }}
      />
    </>
  );

  const lockIcon = (
    <span className="tw-ml-2 tw-inline-block">{NavIcons.lock}</span>
  );

  // AGS SideBar code start

  const navbarOptions: Array<NavigationItemOption | NavigationLinkOption> = [
    {
      title: 'Dashboard',
      icon: NavIcons.dashboard,
      path: createUrl(AuthorizedPaths.dashboard),
      key: 'dashboard',
      isActive: regexTest('dashboard'),
      show: deflectLicense,
    },
    {
      title: 'Plays',
      icon: icons.play,
      path: createUrl(AuthorizedPaths.plays),
      key: 'plays',
      isActive: regexTest('plays'),
      show: playEnabled,
    },
    {
      title: isStarter ? (
        withToolTip(
          strings.UPGRADE_PLAN({
            description: strings.UPGRADE_TO_DEFLECTION_DESC,
          }),
          <div>Offers{lockIcon}</div>
        )
      ) : (
        <>Offers</>
      ),
      icon: NavIcons.loyalty,
      path: createUrl(AuthorizedPaths.offers),
      key: 'offers',
      isActive: regexTest('offers'),
      disabled: isStarter || isExpBrandingLibDisabled,
      disableTooltipText:
        isExpBrandingLibDisabled &&
        'Create your first cancel experience on the get started page to view this section.',
      show: true,
    },
    {
      title: 'Experiences',
      icon: NavIcons.accountTree,
      onClick: () => {
        const newVal = !experiencesExpanded;
        setExperiencesExpanded(newVal);
        if (newVal) {
          history.push(
            additionalBrandsEnabled
              ? createUrl(AuthorizedPaths.additionalBrandsPage)
              : createUrl(AuthorizedPaths.experiencesBrandingPage)
          );
        }
      },
      isActive:
        !regexTest('offers') &&
        (regexTest(AuthorizedPaths.experiencesBrandingPage) ||
          regexTest(AuthorizedPaths.additionalBrandsPage) ||
          regexTest(new RegExp(`^${AuthorizedPaths.experiencesPages}`)) ||
          regexTest('experiences/funnels') ||
          regexTest('experiences/hosted') ||
          regexTest('targeting') ||
          regexTest(AuthorizedPaths.lossAversionList) ||
          regexTest(/experiences(\/funnels\/\d+)?\/page\/reasons$/) ||
          regexTest(AuthorizedPaths.confirmationPages) ||
          experiencesExpanded),
      disabled: isExpBrandingLibDisabled,
      disableTooltipText:
        'Create your first cancel experience on the get started page to view this section.',
      show: true,
      expandChildren: experiencesExpanded,
      children: [
        {
          key: 'branding',
          title: 'Branding',
          path: createUrl(AuthorizedPaths.experiencesBrandingPage),
          icon: NavIcons.photograph,
          isActive: regexTest(AuthorizedPaths.experiencesBrandingPage),
          show: !additionalBrandsEnabled,
          disabled: isExpBrandingLibDisabled,
        },
        {
          title: 'Brands',
          path: createUrl(AuthorizedPaths.additionalBrandsPage),
          icon: NavIcons.photograph,
          disabled: isExpBrandingLibDisabled,
          show: additionalBrandsEnabled,
          isActive: regexTest(AuthorizedPaths.additionalBrandsPage),
          key: 'brands',
        },
        {
          title: 'Pages',
          key: 'pages',
          path: pagesListUrl,
          icon: NavIcons.duplicateDocument,
          isActive:
            regexTest(new RegExp(`^${AuthorizedPaths.experiencesPages}`)) ||
            regexTest('experiences/funnels') ||
            regexTest('experiences/hosted'),
          disabled: agsLicense && !deflectLicense,
          show: deflectLicense,
        },
        {
          title: isStarter ? (
            withToolTip(
              strings.UPGRADE_PLAN({
                description: strings.UPGRADE_TO_DEFLECTION_DESC,
              }),
              <div>Targeting{lockIcon}</div>
            )
          ) : (
            <>
              Targeting
              {expired && lockIcon}
            </>
          ),
          icon: NavIcons.target,
          path: createUrl(AuthorizedPaths.experiencesTargeting),
          key: 'targeting',
          isActive: regexTest('targeting'),
          show: deflectLicense,
          disabled: isStarter || isExpBrandingLibDisabled,
        },
        {
          title: isStarter ? (
            withToolTip(
              strings.UPGRADE_PLAN({
                description: strings.UPGRADE_TO_DEFLECTION_DESC,
              }),
              <div className="tw-flex">Loss aversion cards{lockIcon}</div>
            )
          ) : (
            <>Loss aversion cards</>
          ),
          icon: NavIcons.problem,
          disabled: isStarter || isExpLibraryDisabled,
          path: createUrl(AuthorizedPaths.lossAversionList),
          isActive: regexTest(AuthorizedPaths.lossAversionList),
          show: deflectLicense,
          key: 'loss aversion cards',
        },
        {
          title: 'Survey reasons',
          icon: NavIcons.server,
          disabled: isExpBrandingLibDisabled,
          path: createUrl(AuthorizedPaths.experiencesPageReasons),
          isActive: regexTest(/experiences(\/funnels\/\d+)?\/page\/reasons$/),
          show: deflectLicense,
          key: 'survey reasons',
        },
        {
          title: 'Redirect pages',
          icon: NavIcons.checkCircle,
          path: createUrl(AuthorizedPaths.confirmationPages),
          disabled: isExpBrandingLibDisabled,
          isActive: regexTest(AuthorizedPaths.confirmationPages),
          show: agsLicense || deflectLicense,
          key: 'redirect pages',
        },
      ],
      key: 'experiences',
    },
    {
      title: isStarter ? (
        withToolTip(
          strings.UPGRADE_PLAN({
            description: strings.UPGRADE_TO_DEFLECTION_DESC,
          }),
          <span>Audiences{lockIcon}</span>
        )
      ) : (
        <>
          Audiences
          {expired && lockIcon}
        </>
      ),
      icon: NavIcons.audiences,
      disabled: isStarter || isExpBrandingLibDisabled,
      path: createUrl(AuthorizedPaths.audiencesList),
      key: 'audiences',
      isActive: regexTest('audience'),
      disableTooltipText:
        isExpBrandingLibDisabled &&
        'Create your first cancel experience on the get started page to view this section.',
      show: true,
    },
    {
      title: 'Reports',
      icon: NavIcons.barChart,
      onClick: () => {
        const nextReportsState = !reportsExpanded;
        setReportsExpanded(nextReportsState);
        const nextInsightState = !insightsExpanded;
        setInsightsExpanded(nextInsightState);
        if (nextReportsState || nextInsightState)
          history.push(
            isStarter
              ? createUrl(AuthorizedPaths.insightsCancel)
              : createUrl(AuthorizedPaths.insightsExperiencePerformanceReport)
          );
      },
      key: 'reports',
      isActive:
        regexTest('insight') ||
        regexTest('trends') ||
        regexTest('customers') ||
        reportsExpanded,
      show: deflectLicense,
      expandChildren: reportsExpanded,
      children: [
        {
          title: (
            <>
              Insights
              {expired && lockIcon}
            </>
          ),
          icon: NavIcons.lightBulb,
          show: deflectLicense,
          key: 'insights',
          onClick: () => {
            const nextInsightState = !insightsExpanded;
            setInsightsExpanded(nextInsightState);
            if (nextInsightState)
              history.push(
                isStarter
                  ? createUrl(AuthorizedPaths.insightsCancel)
                  : createUrl(
                      AuthorizedPaths.insightsExperiencePerformanceReport
                    )
              );
          },
          isActive: regexTest('insight') || insightsExpanded,
          expandChildren: insightsExpanded,
          children: [
            {
              title: 'Cancels (AI)',
              path: createUrl(AuthorizedPaths.insightsCancelAI),
              isActive: regexTest(AuthorizedPaths.insightsCancelAI),
              show: isCancelInsightsAIEnabled,
              key: 'cancel_ai',
            },
            {
              title: 'Cancels',
              path: createUrl(AuthorizedPaths.insightsCancel),
              isActive: regexTest(AuthorizedPaths.insightsCancel),
              key: 'cancels',
              show: deflectLicense,
            },
            {
              title: isStarter ? (
                withToolTip(
                  strings.UPGRADE_PLAN({
                    description: strings.UPGRADE_TO_DEFLECTION_DESC,
                  }),
                  <span>Offer Performance{lockIcon}</span>
                )
              ) : (
                <span>Offer Performance</span>
              ),
              path: createUrl(AuthorizedPaths.insightsOfferPerformance),
              isActive: regexTest(AuthorizedPaths.insightsOfferPerformance),
              disabled: isStarter,
              key: 'offer performance',
              show: deflectLicense,
            },
            {
              title: isStarter ? (
                withToolTip(
                  strings.UPGRADE_PLAN({
                    description: strings.UPGRADE_TO_DEFLECTION_DESC,
                  }),
                  <span>Experience Performance{lockIcon}</span>
                )
              ) : (
                <span>Experience Performance</span>
              ),
              path: createUrl(
                AuthorizedPaths.insightsExperiencePerformanceReport
              ),
              isActive: regexTest(
                AuthorizedPaths.insightsExperiencePerformanceReport
              ),
              disabled: isStarter,
              show: deflectLicense,
              key: 'experience performance',
            },
            {
              title: isStarter ? (
                withToolTip(
                  strings.UPGRADE_PLAN({
                    description: strings.UPGRADE_TO_DEFLECTION_DESC,
                  }),
                  <span>Retained Revenue Cohort{lockIcon}</span>
                )
              ) : (
                <span>Retained Revenue Cohort</span>
              ),
              path: createUrl(AuthorizedPaths.insightsRetainedRevenueCohort),
              isActive: regexTest(
                AuthorizedPaths.insightsRetainedRevenueCohort
              ),
              disabled: isStarter,
              show: deflectLicense && Boolean(isRepurchaseAttributionEnabled),
              key: 'retained revenue cohort',
            },
            {
              title: isStarter ? (
                withToolTip(
                  strings.UPGRADE_PLAN({
                    description: strings.UPGRADE_TO_DEFLECTION_DESC,
                  }),
                  <span>Lift Report{lockIcon}</span>
                )
              ) : (
                <span>Lift Report</span>
              ),
              path: createUrl(AuthorizedPaths.insightsLiftDashboard),
              disabled: isStarter,
              isActive: regexTest(AuthorizedPaths.insightsLiftDashboard),
              show: deflectLicense,
              key: 'life report',
            },
          ],
        },
        {
          title: (
            <>
              Trends
              {expired && lockIcon}
            </>
          ),
          icon: NavIcons.trendingUp,
          onClick: () => {
            const nextTrendsState = !trendsExpanded;
            setTrendsExpanded(nextTrendsState);
            if (nextTrendsState)
              history.push(createUrl(AuthorizedPaths.trendsSavePerformance));
          },
          isActive: regexTest('trends') || trendsExpanded,
          show: deflectLicense,
          expandChildren: trendsExpanded,
          key: 'trends',
          children: [
            {
              title: 'Save performance',
              path: createUrl(AuthorizedPaths.trendsSavePerformance),
              isActive: regexTest(AuthorizedPaths.trendsSavePerformance),
              key: 'save performance',
              show: deflectLicense,
            },
            {
              title: isStarter ? (
                withToolTip(
                  strings.UPGRADE_PLAN({
                    description: strings.UPGRADE_TO_DEFLECTION_DESC,
                  }),
                  <span>Deflection{lockIcon}</span>
                )
              ) : (
                <span>Deflection</span>
              ),
              path: createUrl(AuthorizedPaths.trendsDeflection),
              isActive: regexTest(AuthorizedPaths.trendsDeflection),
              disabled: isStarter,
              key: 'deflection',
              show: deflectLicense,
            },
            {
              title: 'Cancels',
              path: createUrl(AuthorizedPaths.trendsCancels),
              isActive: regexTest(AuthorizedPaths.trendsCancels),
              key: 'cancels',
              show: deflectLicense,
            },
            {
              title: isStarterFreePlan ? (
                withToolTip(
                  strings.UPGRADE_PLAN({
                    description: strings.UPGRADE_TO_STARTER_PREMIUM_SAVES_DESC,
                  }),
                  <span>Saves{lockIcon}</span>
                )
              ) : (
                <span>Saves</span>
              ),
              path: createUrl(AuthorizedPaths.trendsSaves),
              isActive: regexTest(AuthorizedPaths.trendsSaves),
              disabled: isStarterFreePlan,
              key: 'saves',
              show: deflectLicense,
            },
          ],
        },
        {
          title: (
            <>
              Engaged subscribers
              {expired && lockIcon}
            </>
          ),
          icon: NavIcons.user,
          onClick: () => {
            const nextCustomersState = !customersExpanded;
            setCustomersExpanded(nextCustomersState);
            if (nextCustomersState)
              history.push(
                createUrl(AuthorizedPaths.customers, {
                  customerView: 'all',
                })
              );
          },
          isActive: regexTest('customers') || customersExpanded,
          show: deflectLicense,
          expandChildren: customersExpanded,
          key: 'customers',
          children: [
            {
              title: 'All',
              path: createUrl(AuthorizedPaths.customers, {
                customerView: 'all',
              }),
              isActive: regexTest('customers/all'),
              key: 'all customers',
              show: deflectLicense,
            },
            {
              title: 'Cancels',
              path: createUrl(AuthorizedPaths.customers, {
                customerView: 'cancels',
              }),
              isActive: regexTest('customers/cancel'),
              key: 'cancel customers',
              show: deflectLicense,
            },
            {
              title: isStarterFreePlan ? (
                withToolTip(
                  strings.UPGRADE_PLAN({
                    description: strings.UPGRADE_TO_STARTER_PREMIUM_SAVES_DESC,
                  }),
                  <div>Saves{lockIcon}</div>
                )
              ) : (
                <>Saves</>
              ),
              path: createUrl(AuthorizedPaths.customers, {
                customerView: 'saves',
              }),
              isActive: regexTest('customers/save'),
              disabled: isStarterFreePlan,
              key: 'save customers',
              show: deflectLicense,
            },
            {
              title: 'Watch list',
              path: createUrl(AuthorizedPaths.customers, {
                customerView: 'watchlist',
              }),
              isActive: regexTest('customers/watchlist'),
              key: 'watchlist customers',
              show: deflectLicense,
            },
            {
              title: 'Lost Deflects',
              path: createUrl(AuthorizedPaths.customers, {
                customerView: 'lostDeflects',
              }),
              isActive: regexTest('customers/lostDeflects'),
              linkAttributes: {
                title: 'Cancels that had a deflect.',
              },
              key: 'lost deflect customers',
              show: deflectLicense,
            },
            {
              title: isStarter ? (
                withToolTip(
                  strings.UPGRADE_PLAN({
                    description: strings.UPGRADE_TO_DEFLECTION_DESC,
                  }),
                  <span>Repurchases{lockIcon}</span>
                )
              ) : (
                <span>Repurchases</span>
              ),
              path: createUrl(AuthorizedPaths.customers, {
                customerView: 'repurchases',
              }),
              isActive: regexTest('customers/repurchase'),
              disabled: isStarter,
              show: deflectLicense && Boolean(isRepurchaseAttributionEnabled),
              key: 'repurchase customers',
            },
            {
              title: isStarter ? (
                withToolTip(
                  strings.UPGRADE_PLAN({
                    description: strings.UPGRADE_TO_DEFLECTION_DESC,
                  }),
                  <span>Winbacks{lockIcon}</span>
                )
              ) : (
                <span>Winbacks</span>
              ),
              path: createUrl(AuthorizedPaths.customers, {
                customerView: 'winbacks',
              }),
              isActive: regexTest('customers/winbacks'),
              disabled: isStarter,
              key: 'winback customers',
              show: deflectLicense,
            },
          ],
        },
      ],
    },
    {
      title: 'Settings',
      key: 'settings',
      icon: NavIcons.settings,
      path: createUrl(AuthorizedPaths.settings),
      isActive: regexTest(AuthorizedPaths.settings),
      show: true,
    },
  ];

  if (agsLicense || (agsLicense && deflectLicense)) {
    return (
      <div
        className={cs(
          'left-column !tw-basis-[18rem] tw-rounded-r-md tw-bg-white tw-shadow-xl',
          {
            'd-none': !showNav || !isFFsSuccess,
            'tw-pb-11': showAlert,
            'tw-pb-4': !showAlert,
          }
        )}
      >
        <div>
          <Link
            className="tw-mb-6 tw-block [&_svg]:tw-h-11 [&_svg]:tw-w-44"
            to={{
              pathname:
                agsLicense && !deflectLicense
                  ? createUrl(AuthorizedPaths.plays)
                  : createUrl(AuthorizedPaths.dashboard),
            }}
            title="Dashboard"
          >
            {showRetentionLogoOnAGS ? <CBRetentionLogo /> : CBGrowthLogoSVG}
          </Link>
          <div className="tw-ml-[-50px] tw-w-[290px] tw-border tw-border-solid tw-border-cbrneutral-200" />
          <ul className="nav-links !tw-mt-6">
            {deflectLicense && showGetStarted && (
              <NavHeader active={regexTest(AuthorizedPaths.getStarted)}>
                <NavLink
                  classes={cs(
                    { active: regexTest(AuthorizedPaths.getStarted) },
                    'nav-link'
                  )}
                  href={AuthorizedPaths.getStarted}
                  text={<GetStartedNav />}
                />
              </NavHeader>
            )}
            {navbarOptions
              .filter((opt) => opt.show)
              .map((section) => {
                const isActive = section.isActive;
                const navItem = (
                  <NavItem
                    disabled={isExpBrandingLibDisabled}
                    classes={cs('nav-link !tw-justify-between', {
                      '!tw-font-semibold !tw-text-black': section.isActive,
                    })}
                    iconNode={section.icon}
                    onClick={(section as NavigationItemOption).onClick}
                    text={section.title}
                    content={
                      <span
                        className={cs({
                          '[&_svg]:tw-rotate-180': (
                            section as NavigationItemOption
                          ).expandChildren,
                        })}
                      >
                        {icons.chevronDown}
                      </span>
                    }
                    headerWrapper
                  />
                );

                const navLink = (
                  <NavLink
                    classes={cs('nav-link', {
                      '!tw-font-semibold !tw-text-black': section.isActive,
                    })}
                    href={(section as NavigationLinkOption).path}
                    iconNode={section.icon as React.ReactElement}
                    text={section.title}
                    disabled={section.disabled}
                  />
                );
                return (
                  <>
                    <li
                      className={cs(
                        'bbk-nav-header tw-my-1 tw-ml-[-10px] tw-rounded tw-px-3 tw-py-2',
                        {
                          'tw-bg-cbrprimary-50':
                            isActive && (section as NavigationLinkOption)?.path,
                        }
                      )}
                    >
                      <span>
                        {(section as NavigationItemOption)?.onClick ? (
                          section.disabled ? (
                            <TippyTooltip content={section.disableTooltipText}>
                              {navItem}
                            </TippyTooltip>
                          ) : (
                            navItem
                          )
                        ) : section.disabled ? (
                          <TippyTooltip content={section.disableTooltipText}>
                            <span> {navLink}</span>
                          </TippyTooltip>
                        ) : (
                          navLink
                        )}
                      </span>
                    </li>
                    <Collapse
                      isOpened={
                        (section as NavigationItemOption).expandChildren
                      }
                    >
                      <div className="tw-pt-3">
                        {(section as NavigationItemOption)?.children
                          ?.filter((opt) => opt?.show ?? true)
                          .map((option) => (
                            <>
                              <li
                                className={cs(
                                  'tw-ml-[-10px] tw-rounded !tw-py-0',
                                  {
                                    'tw-font-semibold tw-text-black':
                                      option.isActive,
                                  }
                                )}
                              >
                                <span>
                                  {(option as NavigationItemOption)?.onClick ? (
                                    <NavItem
                                      disabled={option.disabled}
                                      classes={cs(
                                        'nav-link !tw-py-1 !tw-pl-6 !tw-justify-between',
                                        {
                                          '!tw-font-semibold !tw-text-black':
                                            option.isActive,
                                        }
                                      )}
                                      iconNode={option.icon}
                                      onClick={
                                        (option as NavigationItemOption).onClick
                                      }
                                      text={option.title}
                                      content={
                                        <span
                                          className={cs({
                                            '[&_svg]:tw-rotate-180': (
                                              option as NavigationItemOption
                                            ).expandChildren,
                                          })}
                                        >
                                          {icons.chevronDown}
                                        </span>
                                      }
                                      headerWrapper
                                    />
                                  ) : (
                                    <NavLink
                                      classes={cs(
                                        'nav-link tw-py-2 !tw-pl-10',
                                        {
                                          'tw-bg-cbrprimary-50 !tw-font-semibold !tw-text-black':
                                            option.isActive,
                                        }
                                      )}
                                      href={
                                        (option as NavigationLinkOption).path
                                      }
                                      iconNode={
                                        option.icon as React.ReactElement
                                      }
                                      text={option.title}
                                      disabled={option?.disabled ?? false}
                                    />
                                  )}
                                </span>
                              </li>
                              <Collapse
                                isOpened={
                                  (option as NavigationItemOption)
                                    .expandChildren
                                }
                              >
                                <NavCollapseContainer>
                                  {(option as NavigationItemOption)?.children
                                    ?.filter((opt) => opt?.show ?? true)
                                    .map((opt) => (
                                      <div
                                        className={cs('tw-py-1 tw-pl-[50px]', {
                                          'tw-bg-cbrprimary-50 tw-font-semibold tw-text-black':
                                            opt.isActive,
                                        })}
                                      >
                                        <NavContainer>
                                          <NavLink
                                            href={
                                              (opt as NavigationLinkOption).path
                                            }
                                            text={opt.title}
                                          />
                                        </NavContainer>
                                      </div>
                                    ))}
                                </NavCollapseContainer>
                              </Collapse>
                            </>
                          ))}
                      </div>
                    </Collapse>
                  </>
                );
              })}
          </ul>
        </div>
        <ul className="utilities !tw-mt-4">
          {deflectLicense && showWizardPopup && (
            <Suspense fallback={<SuspenseLoader message=" " />}>
              <WelcomePopup />
            </Suspense>
          )}

          {hasChargebeeIntegration && <CBProductsSwitcher />}
          <div className="tw-mb-3 tw-w-full tw-border-b tw-border-solid tw-border-b-cbrneutral-400" />
          {showInternal && (
            <div className="nav-links">
              <NavContainer>
                <NavLink
                  classes={cs({ active: regexTest('internal') }, 'nav-link')}
                  iconNode={<span>{NavIcons.admin}</span>}
                  text="Admin"
                  href="/internal/companies"
                />
              </NavContainer>
              <Collapse
                isOpened={
                  internalExpanded ||
                  window.location.pathname.includes('/internal/')
                }
              >
                {internalMenu.map((item) => {
                  if (
                    item.hiddenFromNav ||
                    item.featureFlags.filter((ff) => featureFlags.includes(ff))
                      .length === 0
                  ) {
                    return null;
                  }

                  return (
                    <NavContainer key={item.path}>
                      <NavLink
                        classes={cs(
                          {
                            active: regexTest(item.path),
                          },
                          'nav-link nav-sub-link'
                        )}
                        href={item.path}
                        text={
                          item.title /* + (item.component ? ' [REACT]':'') */
                        }
                        linkAttributes={{
                          target: item.target,
                          rel: item.rel,
                        }}
                      />
                    </NavContainer>
                  );
                })}
              </Collapse>
            </div>
          )}
          {helpDocSection}
          {contactSupportSection}
          {userProfileSection}
          {appSwitcherSection}
        </ul>
      </div>
    );
  }

  // AGS Sidebar code end

  return (
    <div
      className={cs('left-column', {
        'd-none': !showNav || !isFFsSuccess,
        'tw-pb-11': showAlert,
        'tw-pb-4': !showAlert,
      })}
    >
      <div>
        <Link
          className="tw-mb-6 tw-block"
          to={{ pathname: createUrl(AuthorizedPaths.dashboard) }}
          title="Dashboard"
        >
          <CBRetentionLogo />
        </Link>
        <ul className="nav-links">
          {showGetStarted && (
            <NavHeader active={regexTest(AuthorizedPaths.getStarted)}>
              <NavLink
                classes={cs(
                  { active: regexTest(AuthorizedPaths.getStarted) },
                  'nav-link'
                )}
                href={AuthorizedPaths.getStarted}
                text={<GetStartedNav />}
              />
            </NavHeader>
          )}
          <NavHeader active={regexTest(AuthorizedPaths.dashboard)}>
            <NavLink
              classes={cs({ active: regexTest('dashboard') }, 'nav-link')}
              href={AuthorizedPaths.dashboard}
              icon="gauge"
              text="Dashboard"
            />
          </NavHeader>
          <div className="tw-relative">
            {isExpBrandingLibDisabled && (
              <TippyTooltip
                tippyOptions={{
                  placement: 'right',
                  offset: [0, -150],
                  arrow: false,
                  appendTo: () => document.body,
                }}
                content="Create your first cancel experience on the get started page to view this section."
              >
                <div
                  className={cs(
                    'tw-absolute tw-z-10',
                    'tw-bottom-0 tw-left-0 tw-right-0 tw-top-0'
                  )}
                />
              </TippyTooltip>
            )}
            {playEnabled && (
              <NavHeader active={regexTest(AuthorizedPaths.plays)}>
                <NavLink
                  classes={cs({ active: regexTest('plays') }, 'nav-link')}
                  href={AuthorizedPaths.plays}
                  icon="play"
                  text="Play"
                />
              </NavHeader>
            )}
            <NavHeader active={regexTest('experiences')}>
              <NavItem
                disabled={isExpBrandingLibDisabled}
                title="Experiences"
                classes={cs({ active: regexTest('experiences') }, 'nav-link')}
                iconNode={NavIcons.experiences}
                onClick={() => {
                  const newVal = !experiencesExpanded;
                  setExperiencesExpanded(newVal);
                  if (newVal) {
                    history.push(
                      additionalBrandsEnabled
                        ? createUrl(AuthorizedPaths.additionalBrandsPage)
                        : createUrl(AuthorizedPaths.experiencesBrandingPage)
                    );
                  }
                }}
                text={<span>Experiences</span>}
              />
            </NavHeader>
            <Collapse
              isOpened={
                isExpBrandingLibDisabled ||
                experiencesExpanded ||
                regexTest('/get-started')
              }
            >
              <NavCollapseContainer>
                <NavSubHeader>Manage</NavSubHeader>
                <NavContainer>
                  {additionalBrandsEnabled ? (
                    <NavLink
                      disabled={isExpLibraryDisabled}
                      classes={cs(
                        {
                          active: regexTest(
                            AuthorizedPaths.additionalBrandsPage
                          ),
                        },
                        'nav-link nav-sub-link'
                      )}
                      href={AuthorizedPaths.additionalBrandsPage}
                      text={strings.BRANDS_DISPLAY_NAME}
                      iconNode={NavIcons.pages}
                    />
                  ) : (
                    <NavLink
                      disabled={isExpBrandingLibDisabled}
                      classes={cs(
                        {
                          active: regexTest(
                            AuthorizedPaths.experiencesBrandingPage
                          ),
                        },
                        'nav-link nav-sub-link'
                      )}
                      href={AuthorizedPaths.experiencesBrandingPage}
                      text={strings.BRANDING_DISPLAY_NAME}
                      iconNode={NavIcons.pages}
                    />
                  )}
                </NavContainer>
                <NavContainer>
                  <NavLink
                    disabled={isExpBrandingLibDisabled}
                    classes={cs(
                      {
                        active:
                          regexTest(
                            new RegExp(`^${AuthorizedPaths.experiencesPages}`)
                          ) ||
                          regexTest('experiences/funnels') ||
                          regexTest('experiences/hosted'),
                      },
                      'nav-link nav-sub-link'
                    )}
                    href={pagesListUrl}
                    text="Pages"
                    iconNode={NavIcons.pages}
                  />
                </NavContainer>
                <NavContainer>
                  <NavLink
                    disabled={isStarter || isExpLibraryDisabled}
                    classes={cs(
                      {
                        active: regexTest(AuthorizedPaths.experiencesTargeting),
                      },
                      'nav-link nav-sub-link'
                    )}
                    href={AuthorizedPaths.experiencesTargeting}
                    text={
                      isStarter ? (
                        withToolTip(
                          strings.UPGRADE_PLAN({
                            description: strings.UPGRADE_TO_DEFLECTION_DESC,
                          }),
                          <div>Targeting{lockIcon}</div>
                        )
                      ) : (
                        <>
                          Targeting
                          {expired && lockIcon}
                        </>
                      )
                    }
                    iconNode={NavIcons.bullseye}
                  />
                </NavContainer>

                <NavSubHeader className="tw-mt-4">Libraries</NavSubHeader>
                <NavContainer>
                  <NavLink
                    classes={cs(
                      {
                        active: regexTest(AuthorizedPaths.offers),
                      },
                      'nav-link nav-sub-link'
                    )}
                    iconNode={NavIcons.offers}
                    href={AuthorizedPaths.offers}
                    disabled={isStarter || isExpLibraryDisabled}
                    text={
                      isStarter ? (
                        withToolTip(
                          strings.UPGRADE_PLAN({
                            description: strings.UPGRADE_TO_DEFLECTION_DESC,
                          }),
                          <div>Offers{lockIcon}</div>
                        )
                      ) : (
                        <>Offers</>
                      )
                    }
                  />
                </NavContainer>
                <NavContainer>
                  <NavLink
                    classes={cs(
                      {
                        active: regexTest(AuthorizedPaths.lossAversionList),
                      },
                      'nav-link nav-sub-link'
                    )}
                    iconNode={NavIcons.lossAversions}
                    href={AuthorizedPaths.lossAversionList}
                    disabled={isStarter || isExpLibraryDisabled}
                    text={
                      isStarter ? (
                        withToolTip(
                          strings.UPGRADE_PLAN({
                            description: strings.UPGRADE_TO_DEFLECTION_DESC,
                          }),
                          <div className="tw-flex">
                            Loss aversion cards{lockIcon}
                          </div>
                        )
                      ) : (
                        <>Loss aversion cards</>
                      )
                    }
                  />
                </NavContainer>
                <NavContainer>
                  <NavLink
                    disabled={isExpBrandingLibDisabled}
                    classes={cs(
                      {
                        active: regexTest(
                          /experiences(\/funnels\/\d+)?\/page\/reasons$/
                        ),
                      },
                      'nav-link nav-sub-link'
                    )}
                    iconNode={NavIcons.reasons}
                    href={AuthorizedPaths.experiencesPageReasons}
                    text="Survey reasons"
                  />
                </NavContainer>
                <NavContainer>
                  <NavLink
                    disabled={isExpBrandingLibDisabled}
                    classes={cs(
                      {
                        active: regexTest(AuthorizedPaths.confirmationPages),
                      },
                      'nav-link nav-sub-link'
                    )}
                    iconNode={NavIcons.confirmationPages}
                    href={AuthorizedPaths.confirmationPages}
                    text="Redirect pages"
                  />
                </NavContainer>
              </NavCollapseContainer>
            </Collapse>
            <NavHeader active={regexTest('audiences')}>
              <NavItem
                disabled={isExpLibraryDisabled || isStarter}
                title="Audiences"
                classes={cs({ active: regexTest('audiences') }, 'nav-link')}
                iconNode={NavIcons.audiences}
                onClick={() => {
                  if (!isStarter) {
                    history.push(createUrl(AuthorizedPaths.audiencesList));
                  }
                }}
                text={
                  isStarter ? (
                    withToolTip(
                      strings.UPGRADE_PLAN({
                        description: strings.UPGRADE_TO_DEFLECTION_DESC,
                      }),
                      <span>Audiences{lockIcon}</span>
                    )
                  ) : (
                    <>
                      Audiences
                      {expired && lockIcon}
                    </>
                  )
                }
              />
            </NavHeader>
          </div>
          <NavHeader active={regexTest('insights')}>
            <NavItem
              title="Insights"
              classes={cs(
                {
                  active: regexTest('insights'),
                },
                'nav-link',
                'nav-insights'
              )}
              icon="insights"
              onClick={() => {
                const newVal = !insightsExpanded;
                setInsightsExpanded(newVal);
                if (newVal)
                  history.push(
                    isStarter
                      ? createUrl(AuthorizedPaths.insightsCancel)
                      : createUrl(
                          AuthorizedPaths.insightsExperiencePerformanceReport
                        )
                  );
              }}
              text={
                <>
                  Insights
                  {expired && lockIcon}
                </>
              }
            />
          </NavHeader>
          <Collapse isOpened={insightsExpanded}>
            <NavCollapseContainer>
              {isCancelInsightsAIEnabled && (
                <NavContainer>
                  <NavLink
                    classes={cs(
                      { active: regexTest(AuthorizedPaths.insightsCancelAI) },
                      'nav-link nav-sub-link'
                    )}
                    href={AuthorizedPaths.insightsCancelAI}
                    text="Cancels (AI)"
                  />
                </NavContainer>
              )}
              <NavContainer>
                <NavLink
                  classes={cs(
                    { active: regexTest(AuthorizedPaths.insightsCancel) },
                    'nav-link nav-sub-link'
                  )}
                  href={AuthorizedPaths.insightsCancel}
                  text="Cancels"
                />
              </NavContainer>
              <NavContainer>
                <NavLink
                  disabled={isStarter}
                  classes={cs(
                    {
                      active: regexTest(
                        AuthorizedPaths.insightsOfferPerformance
                      ),
                    },
                    'nav-link nav-sub-link'
                  )}
                  href={AuthorizedPaths.insightsOfferPerformance}
                  text={
                    isStarter ? (
                      withToolTip(
                        strings.UPGRADE_PLAN({
                          description: strings.UPGRADE_TO_DEFLECTION_DESC,
                        }),
                        <span>Offer Performance{lockIcon}</span>
                      )
                    ) : (
                      <span>Offer Performance</span>
                    )
                  }
                />
              </NavContainer>
              <NavContainer>
                <NavLink
                  disabled={isStarter}
                  classes={cs(
                    {
                      active: regexTest(
                        AuthorizedPaths.insightsExperiencePerformanceReport
                      ),
                    },
                    'nav-link nav-sub-link'
                  )}
                  href={AuthorizedPaths.insightsExperiencePerformanceReport}
                  text={
                    isStarter ? (
                      withToolTip(
                        strings.UPGRADE_PLAN({
                          description: strings.UPGRADE_TO_DEFLECTION_DESC,
                        }),
                        <span>Experience Performance{lockIcon}</span>
                      )
                    ) : (
                      <span>Experience Performance</span>
                    )
                  }
                />
              </NavContainer>
              {isRepurchaseAttributionEnabled && (
                <NavContainer>
                  <NavLink
                    disabled={isStarter}
                    classes={cs(
                      {
                        active: regexTest(
                          AuthorizedPaths.insightsRetainedRevenueCohort
                        ),
                      },
                      'nav-link nav-sub-link'
                    )}
                    href={AuthorizedPaths.insightsRetainedRevenueCohort}
                    text={
                      isStarter ? (
                        withToolTip(
                          strings.UPGRADE_PLAN({
                            description: strings.UPGRADE_TO_DEFLECTION_DESC,
                          }),
                          <span>Retained Revenue Cohort{lockIcon}</span>
                        )
                      ) : (
                        <span>Retained Revenue Cohort</span>
                      )
                    }
                  />
                </NavContainer>
              )}
              <NavContainer>
                <NavLink
                  disabled={isStarter}
                  classes={cs(
                    {
                      active: regexTest(AuthorizedPaths.insightsLiftDashboard),
                    },
                    'nav-link nav-sub-link'
                  )}
                  href={AuthorizedPaths.insightsLiftDashboard}
                  text={
                    isStarter ? (
                      withToolTip(
                        strings.UPGRADE_PLAN({
                          description: strings.UPGRADE_TO_DEFLECTION_DESC,
                        }),
                        <span>Lift Report{lockIcon}</span>
                      )
                    ) : (
                      <span>Lift Report</span>
                    )
                  }
                />
              </NavContainer>
            </NavCollapseContainer>
          </Collapse>
          <NavHeader active={regexTest('trends')}>
            <NavItem
              title="Trends"
              classes={cs(
                { active: regexTest('trends') },
                'nav-link',
                'nav-trends'
              )}
              icon="trends"
              onClick={() => {
                const newVal = !trendsExpanded;
                setTrendsExpanded(newVal);
                if (newVal) {
                  history.push(createUrl(AuthorizedPaths.trendsCancels));
                }
              }}
              text={
                <>
                  Trends
                  {expired && lockIcon}
                </>
              }
            />
          </NavHeader>
          <Collapse isOpened={trendsExpanded}>
            <NavCollapseContainer>
              <NavContainer>
                <NavLink
                  classes={cs(
                    {
                      active: regexTest(AuthorizedPaths.trendsSavePerformance),
                    },
                    'nav-link nav-sub-link'
                  )}
                  href={AuthorizedPaths.trendsSavePerformance}
                  text="Save performance"
                />
              </NavContainer>
              <NavContainer>
                <NavLink
                  disabled={isStarter}
                  classes={cs(
                    {
                      active: regexTest(AuthorizedPaths.trendsDeflection),
                    },
                    'nav-link nav-sub-link'
                  )}
                  href={AuthorizedPaths.trendsDeflection}
                  text={
                    isStarter ? (
                      withToolTip(
                        strings.UPGRADE_PLAN({
                          description: strings.UPGRADE_TO_DEFLECTION_DESC,
                        }),
                        <span>Deflection{lockIcon}</span>
                      )
                    ) : (
                      <span>Deflection</span>
                    )
                  }
                />
              </NavContainer>
              <NavContainer>
                <NavLink
                  classes={cs(
                    {
                      active: regexTest(AuthorizedPaths.trendsCancels),
                    },
                    'nav-link nav-sub-link'
                  )}
                  href={AuthorizedPaths.trendsCancels}
                  text="Cancels"
                />
              </NavContainer>
              <NavContainer>
                <NavLink
                  disabled={isStarterFreePlan}
                  classes={cs(
                    {
                      active: regexTest(AuthorizedPaths.trendsSaves),
                    },
                    'nav-link nav-sub-link'
                  )}
                  href={AuthorizedPaths.trendsSaves}
                  text={
                    isStarterFreePlan ? (
                      withToolTip(
                        strings.UPGRADE_PLAN({
                          description:
                            strings.UPGRADE_TO_STARTER_PREMIUM_SAVES_DESC,
                        }),
                        <span>Saves{lockIcon}</span>
                      )
                    ) : (
                      <span>Saves</span>
                    )
                  }
                />
              </NavContainer>
            </NavCollapseContainer>
          </Collapse>
          <NavHeader active={regexTest('customers')}>
            <NavItem
              title="Customers"
              classes={cs(
                {
                  active: regexTest('customers'),
                },
                'nav-link',
                'nav-customers'
              )}
              icon="customers"
              text={
                <>
                  Customers
                  {expired && lockIcon}
                </>
              }
              onClick={() => {
                const newVal = !customersExpanded;
                setCustomersExpanded(newVal);
                if (newVal) {
                  history.push(
                    createUrl(AuthorizedPaths.customers, {
                      customerView: 'all',
                    })
                  );
                }
              }}
            />
          </NavHeader>
          <Collapse isOpened={customersExpanded}>
            <NavCollapseContainer>
              <NavContainer>
                <NavLink
                  classes={cs(
                    {
                      active: regexTest(`customers/all`),
                    },
                    'nav-link nav-sub-link'
                  )}
                  href="/customers/all"
                  text="All"
                />
              </NavContainer>
              <NavContainer>
                <NavLink
                  classes={cs(
                    {
                      active: regexTest(`customers/cancel`),
                    },
                    'nav-link nav-sub-link'
                  )}
                  href="/customers/cancels"
                  text="Cancels"
                />
              </NavContainer>
              <NavContainer>
                <NavLink
                  classes={cs(
                    {
                      active: regexTest(`customers/save`),
                    },
                    'nav-link nav-sub-link'
                  )}
                  href="/customers/saves"
                  disabled={isStarterFreePlan}
                  text={
                    isStarterFreePlan ? (
                      withToolTip(
                        strings.UPGRADE_PLAN({
                          description:
                            strings.UPGRADE_TO_STARTER_PREMIUM_SAVES_DESC,
                        }),
                        <div>Saves{lockIcon}</div>
                      )
                    ) : (
                      <>Saves</>
                    )
                  }
                />
              </NavContainer>
              <NavContainer>
                <NavLink
                  classes={cs(
                    {
                      active: regexTest(`customers/watchlist`),
                    },
                    'nav-link nav-sub-link'
                  )}
                  href="/customers/watchlist"
                  text="Watch list"
                />
              </NavContainer>
              <NavContainer>
                <NavLink
                  classes={cs(
                    {
                      active: regexTest(`customers/lostDeflects`),
                    },
                    'nav-link nav-sub-link'
                  )}
                  href="/customers/lostDeflects"
                  text="Lost Deflects"
                  linkAttributes={{
                    title: 'Cancels that had a deflect.',
                  }}
                />
              </NavContainer>
              {isRepurchaseAttributionEnabled && (
                <>
                  <NavContainer>
                    <NavLink
                      disabled={isStarter}
                      classes={cs(
                        {
                          active: regexTest(`customers/repurchases`),
                        },
                        'nav-link nav-sub-link'
                      )}
                      href="/customers/repurchases"
                      text={
                        isStarter ? (
                          withToolTip(
                            strings.UPGRADE_PLAN({
                              description: strings.UPGRADE_TO_DEFLECTION_DESC,
                            }),
                            <span>Repurchases{lockIcon}</span>
                          )
                        ) : (
                          <span>Repurchases</span>
                        )
                      }
                    />
                  </NavContainer>

                  <NavContainer>
                    <NavLink
                      disabled={isStarter}
                      classes={cs(
                        {
                          active: regexTest(`customers/winbacks`),
                        },
                        'nav-link nav-sub-link'
                      )}
                      href="/customers/winbacks"
                      text={
                        isStarter ? (
                          withToolTip(
                            strings.UPGRADE_PLAN({
                              description: strings.UPGRADE_TO_DEFLECTION_DESC,
                            }),
                            <span>Winbacks{lockIcon}</span>
                          )
                        ) : (
                          <span>Winbacks</span>
                        )
                      }
                    />
                  </NavContainer>
                </>
              )}
            </NavCollapseContainer>
          </Collapse>
        </ul>
      </div>
      <ul className="utilities">
        {showWizardPopup && (
          <Suspense fallback={<SuspenseLoader message=" " />}>
            <WelcomePopup />
          </Suspense>
        )}

        {showInternal && (
          <div className="nav-links">
            <NavContainer>
              <NavLink
                classes={cs({ active: regexTest('internal') }, 'nav-link')}
                icon="cbRetention2023Grayscale"
                text="Retention"
                href="/internal/companies"
              />
            </NavContainer>
            <Collapse
              isOpened={
                internalExpanded ||
                window.location.pathname.includes('/internal/')
              }
            >
              {internalMenu.map((item) => {
                if (
                  item.hiddenFromNav ||
                  item.featureFlags.filter((ff) => featureFlags.includes(ff))
                    .length === 0
                ) {
                  return null;
                }

                return (
                  <NavContainer key={item.path}>
                    <NavLink
                      classes={cs(
                        {
                          active: regexTest(item.path),
                        },
                        'nav-link nav-sub-link'
                      )}
                      href={item.path}
                      text={item.title /* + (item.component ? ' [REACT]':'') */}
                      linkAttributes={{
                        target: item.target,
                        rel: item.rel,
                      }}
                    />
                  </NavContainer>
                );
              })}
            </Collapse>
          </div>
        )}
        {hasChargebeeIntegration && <CBProductsSwitcher />}
        {!isStarter && (
          <NavContainer>
            <NavItem
              title="Tour"
              classes="nav-link"
              text="Tour"
              icon="flag"
              onClick={() =>
                dispatch(
                  appNotPersistedSlice.actions.saveMisc({
                    showProductTour: true,
                  })
                )
              }
            />
            <ProductTourModal />
          </NavContainer>
        )}
        {helpDocSection}
        {userProfileSection}
        <NavContainer>
          <NavLink
            classes={cs({ active: regexTest('settings') }, 'nav-link')}
            icon="shield"
            href={AuthorizedPaths.settings}
            text="Settings"
          />
        </NavContainer>
        {appSwitcherSection}
      </ul>
      {showAlert && <BottomAlertTab />}
    </div>
  );
};

export default LeftColumn;
